<template>
  <div class="grid-main-offer" :class="[`grid-main-offer--${theme}`]">
    <div class="grid-main-offer__bottom">
      <div class="grid-main-offer__left">
        <div class="grid-main-offer__price-box-wrapper">
          <div v-if="auction" class="auction-price">
            <div
              v-if="oldPrice"
              class="auction-price__text"
            >
              {{ $t('auction.old_price_text') }}
            </div>
            <div
              v-if="oldPrice"
              class="auction-price__old-price"
              :class="{
                'auction-price__old-price--out-of-stock': !inStock,
              }"
            >
              {{ getFormattedPrice(oldPrice) }}
              <span class="icon-hryvnia" />
            </div>

            <div class="auction-price__text">
              {{ $t('auction.new_price_text') }}
            </div>
            <div
              class="auction-price__price"
              :class="{
                'auction-price__price--out-of-stock': !inStock,
                'auction-price__price--same-old-price': offer.price_promo === offer.price_old && offer.price === offer.price_old,
              }"
            >
              {{ getFormattedPrice(currentPrice) }}
              <span class="icon-hryvnia" />
            </div>
          </div>

          <div v-else class="grid-main-offer__price-box">
            <div
              v-if="oldPrice && inStock"
              class="grid-main-offer__old-price"
            >
              <span
                class="grid-main-offer__old-price__number"
                :class="{
                  'grid-main-offer__old-price__number--out-of-stock': !inStock,
                }"
              >
                {{ getFormattedPrice(oldPrice) }}
              </span>
              <span class="icon-hryvnia" />
            </div>

            <div
              class="grid-main-offer__main-price"
              :class="{
                'grid-main-offer__main-price--new': oldPrice,
                'grid-main-offer__main-price--out-of-stock': !inStock,
              }"
            >
              <template v-if="offer.price">
                {{ getFormattedPrice(currentPrice) }}
                <span class="icon-hryvnia" />
              </template>

              <div v-if="wasSold" class="grid-main-offer__out-of-stock">
                {{ $t('offers.sold') }}
              </div>

              <div v-else-if="!inStock" class="grid-main-offer__out-of-stock">
                {{ $t('offers.not_available') }}
              </div>
            </div>
          </div>
        </div>

        <OfferDiscount
          v-if="showDiscount"
          class="grid-main-offer__discount show-from-desktop"
          theme="small"
          :metric-block="metricBlock"
          :offer-uuid="offer.uuid"
          :offer-code="offer.code"
          :offer-price="currentPrice"
          :max-bonuses-percent="offer.delivery.max_bonuses_percent"
          :product="product"
        />
      </div>

      <div class="grid-main-offer__right">
        <template v-if="isWishlistPage">
          <span
            v-if="!inStock"
            class="grid-main-offer__delete icon-delete"
            @click="handleRemoveFromWishlist"
          />

          <CommonPopover v-else v-model="isWishlistOptionsVisible">
            <template #trigger>
              <span class="icon-vertical-dots options" />
            </template>
            <span class="options-wrapper">
              <span v-if="inStock" class="options-wrapper__option">
                <AddToCart
                  theme="inline"
                  :offer-id="offer.uuid"
                  :offer-code="offer.code"
                  :product="product"
                  @click="isWishlistOptionsVisible = false"
                >
                  {{ isAddedOffer ? $t('product.in_cart') : $t('product.add_to_cart') }}
                </AddToCart>
              </span>
              <span
                class="options-wrapper__option"
                @click="handleRemoveFromWishlist"
              >
                {{ $t('common.delete') }}
              </span>
            </span>
          </CommonPopover>
        </template>

        <template v-else>
          <client-only>
            <WishlistToggler
              v-if="inStock && !hideWishlist"
              class="grid-main-offer__wishlist-button"
              :product-code="product.p_code"
              :product-name="product.name"
              :product-category-slug="product.category.slug"
              :product-slug="product.slug"
              :offer-code="product.offer_main.code"
              :hovered="hovered"
            />
          </client-only>

          <AddToCart
            v-if="inStock"
            :class="{
              'grid-main-offer__add-to-cart--grid': theme !== 'list',
              'grid-main-offer__add-to-cart--list': theme === 'list' && oldPrice
            }"
            :offer-id="offer.uuid"
            :offer-code="offer.code"
            :product="product"
            :disabled="hideAddToCart"
          />
        </template>
      </div>
    </div>

    <OfferDiscount
      v-if="showDiscount"
      class="grid-main-offer__discount show-until-desktop"
      theme="small"
      :metric-block="metricBlock"
      :offer-uuid="offer.uuid"
      :offer-code="offer.code"
      :offer-price="currentPrice"
      :max-bonuses-percent="offer.delivery.max_bonuses_percent"
      :product="product"
    />
    <OfferPromoCampaign
      v-if="offer.price_promo && showPriceCampaign"
      :price="offer.price_promo"
      :price-old="oldPrice"
      :finishes-at="offer.promo?.finishes_at"
      used-in-catalog
      class="grid-main-offer__promo"
    />
  </div>
</template>

<script lang="ts" setup>
import type { OfferCatalog } from '~/types/offer'
import type { ProductCatalog } from '~/types/product'

type Props = {
  theme?: 'default' | 'list'
  offer: OfferCatalog
  metricBlock: string
  product: ProductCatalog
  isWishlistPage?: boolean
  hovered?: boolean
  showBonusesPay?: boolean
  inStock: boolean
  wasSold: boolean
  hideWishlist: boolean
  hideAddToCart: boolean
  auction?: boolean // Change price box view
}
type Emits = {
  (eventName: 'remove-from-wishlist', value: string): void
}

const props = withDefaults(defineProps<Props>(), {
  theme: 'default',
  hovered: false,
  showBonusesPay: false,
  isWishlistPage: false,
  auction: false
})
const emit = defineEmits<Emits>()

const { $dayjs } = useNuxtApp()

const commonMetricsStore = useCommonMetricsStore()
const cartStore = useCartStore()

const isWishlistOptionsVisible = shallowRef(false)

// Computed
const showDiscount = computed(() => {
  return Boolean(
    props.showBonusesPay && props.offer.delivery?.max_bonuses_percent
  )
})

const currentPrice = computed<number>(() => props.offer.price_promo || props.offer.price || 0)

const oldPrice = computed<number>(() => {
  if (props.offer?.price_old && props.offer.price_old > props.offer.price) return props.offer.price_old
  if (props.offer?.price_promo) return props.offer.price
  return 0
})

const isAddedOffer = computed(() => cartStore.addedOffer(props.offer?.uuid))

const showPriceCampaign = computed(() => {
  if (!props.offer?.price_promo) return false
  if (!props.offer?.promo?.finishes_at) return false
  
  if (!oldPrice.value || props.offer.price_promo / oldPrice.value > 0.9) return false // Не показуємо знижку меньше 10%
  return !$dayjs().isAfter($dayjs(props.offer.promo.finishes_at))
})

// Methods
const handleRemoveFromWishlist = () => {
  isWishlistOptionsVisible.value = false
  emit('remove-from-wishlist', props.product.offer_main.code)

  commonMetricsStore.addMetricEvent({
    element: 'ms[wishlist-remove-product]',
    event_type: 'click',
    event_content: ''
  })
}
</script>

<style lang="scss" scoped>
.grid-main-offer {
  $root: &;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex-grow: 1;
  height: 100%;

  &__top {
    max-height: 31px;
    height: 100%;
  }

  &__bottom {
    display: flex;
    justify-content: space-between;
    //padding-top: .9rem;
    align-items: center;
    min-height: 40px;
    max-height: 69px;
    @media only screen and (max-width: 768px) {
      flex-grow: 1;
    }
  }

  &__brand {
    font-size: 1.2rem;
    color: $secondary-font-color;
    margin-top: 0.4rem;
    line-height: 1;
  }

  &__condition {
    margin-top: 0.7rem;
  }

  &__price-box-wrapper {
    @media only screen and (max-width: 768px) {
      display: flex;
      flex-direction: column;
      justify-content: center;
      min-height: 4rem;
    }
  }

  &__price-box {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }

  &__main-price {
    margin-top: 2px;
    font-size: 2.1rem;
    font-weight: 700;
    line-height: 2.2rem;
    letter-spacing: -0.421052px;

    @media only screen and (max-width: 768px) {
      font-size: 1.6rem;
    }

    &--new {
      line-height: 2rem;
      color: $label-red-color;
    }

    &--out-of-stock {
      color: $secondary-font-color;
    }

    sup {
      font-size: 1.4rem;
      font-weight: 700;
      vertical-align: unset;
      text-transform: lowercase;
    }
  }

  &__old-price {
    position: relative;
    color: $secondary-font-color;
    font-weight: 700;
    line-height: 1;

    @media only screen and (max-width: 768px) {
      font-size: 1.2rem;
    }

    &__number {
      text-decoration: line-through;
      font-weight: 500;

      &--out-of-stock {
        color: $secondary-font-color;
      }
    }
  }

  &__out-of-stock {
    font-size: 10px;
    font-weight: 400;
    line-height: 11px;

    @media only screen and (min-width: 768px) {
      font-size: 14px;
      line-height: 16px;
      margin-top: 3px;
    }
  }

  &__left {
    display: flex;
    flex-direction: column;
    @media only screen and (max-width: 768px) {
      height: 100%;
    }
  }

  &__right {
    display: flex;
    flex-direction: row;
    align-items: center;
  }

  &__delete {
    color: #B9B9B9;
    font-size: 20px;
    cursor: pointer;
  }

  &__wishlist-button {
    // для десктопу, кнопка завжди позіціонується відносно контейнеру
    @media only screen and (min-width: 1024px) {
      position: absolute;
      top: 1.6rem;
      right: 1.6rem;
    }

    @media only screen and (max-width: 1024px) {
      top: 0.8rem;
      right: 0.8rem;
      font-size: 2rem;
      position: absolute;
    }
  }

  &__promo {
    margin-top: 0.8rem;
  }

  &--list {
    #{$root} {
      &__left {
        height: initial;
      }

      &__main-price {
        line-height: 2rem;
        font-size: 2rem;
      }

      &__old-price {
        font-size: 1.2rem;
      }

      &__price-box-wrapper {
        display: initial;
        min-height: initial;
      }

      &__bottom {
        padding-top: .2rem;
      }

      &__wishlist-button {
        @media screen and (max-width: 1024px) {
          display: none;
        }
      }
    }

    .grid-main-offer__add-to-cart {
      &--grid {
        border: 1px solid $border-color;
        font-size: 1.8rem;
      }

      &--list {
        @media only screen and (max-width: 1024px) {
          position: relative;
          top: 6px;
        }
      }
    }
  }
}

.options {
  height: 4rem;
  width: 4rem;
  cursor: pointer;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  color: #a2a2a2;
  font-size: 2rem;
}

.options-wrapper {
  display: inline-block;
  padding: 8px 0;

  &__option {
    white-space: nowrap;
    font-size: 1.4rem;
    line-height: 1.8rem;
    padding: 8px 16px;
    cursor: pointer;
    display: block;
    color: $secondary-font-color;

    &:hover {
      color: $font-color;
    }
  }
}

.auction-price {
  @media screen and (max-width: 480px) {
    margin-top: 4px;
  }

  @media screen and (min-width: 480px) {
    display: grid;
    grid-template-columns: 1fr 2fr;
    grid-template-rows: repeat(2, 1fr);
    grid-column-gap: 12px;
    grid-row-gap: 5px;
  }

  &__text {
    color: #8A8A8A;
    font-size: 12px;
  }

  &__price {
    color: #009939;
    font-size: 18px;
    font-weight: 700;

    &--out-of-stock {
      color: $secondary-font-color;
    }

    &--same-old-price {
      color: #161616;
    }
  }

  &__old-price {
    color: #8A8A8A;
    font-size: 14px;
    font-weight: 500;
  }
}
</style>
